import { request } from '@nextgen-web-framework/core';
import type { HttpAugments } from '@nextgen-web-framework/core';
import { CustomRequestOptions } from '../../types';
import type { IOrderConfirmationResponse, OrderHistoryRequest, Forbidden, AllOrderRequest } from '../../types';
import {
  getOrderDataReqObj,
  getOrderHistoryReqObj,
  getSearchOrderReqObj,
  getOrderDetailsReqObj,
  getOrderCancellationReasonsReqObj,
  orderCancellationReqObj,
  getReturnReasonsReqObj,
  returnCancellationReqObj,
} from '../config';
import {
  OrderHistoryResponse,
  OrderDetailResponse,
  CancelOrderResponse,
  CancelOrderRequest,
  OrderReasonResponse,
  CreateReturnOrderResponse,
  ReturnOrderRequest,
  ReturnOrderDetailResponse,
  ReturnOrderHistoryResponse,
  ReturnOrderDataResponse,
  NextNumbersResponse,
} from '../models';
import { ReturnEligibleItemsResponse } from '../models/ReturnEligibleItemsResponse';
import {
  createReturnDraftReqObj,
  getReturnNumberReqObj,
  orderReturnDetailsReqObj,
  orderReturnEligibleReqObj,
  returnOrderImagesDeleteReqObj,
  returnOrderImagesRetrieveReqObj,
  returnOrderImagesUploadReqObj,
  createReturnReqObj,
  getReturnHistoryReqObj,
  orderReturnDataReqObj,
  getDeliveryTrackingReqObj,
  getPacakgeDetailsReqObj,
  getInvoiceDocumentReqObj,
  getAnonymousDeliveryTrackingReqObj,
  getSearchReturnOrderReqObj,
  managePackageActionReqObj,
  saveOrdersPrintStatusReqObj,
  getOrdersPrintStatusReqObj,
  getLatamDeliveryTrackingReqObj,
  getProductConditionsReqObj,
} from '../config/postpurchaseApiEndPoints';
import { UploadReturnImageResponse } from '../models/UploadReturnImageResponse';
import { ReturnImageResponseInner } from '../models/ReturnImageResponseInner';
import { ReturnImageRequest } from '../models/ReturnImageRequest';
import { DeleteReturnImageResponse } from '../models/DeleteReturnImageResponse';
import { DeleteReturnImageRequest } from '../models/DeleteReturnImageRequest';
import { TrackDeliveryResponse } from '../models/TrackDeliveryResponse';
import {
  DeliveryTrackingRequest,
  LatamDeliveryTrackingRequest,
  OrderReturnReasonParams,
} from '../../types/postpurchaseTypes';
import { OrderConsignmentResponse } from '../models/OrderConsignmentResponse';
import { CancelReturnRequest } from '../models/CancelReturnRequest';
import { CancelReturnResponse } from '../models/CancelReturnResponse';
import { InvoiceDocumentResponse } from '../models/InvoiceDocumentResponse';
import { OrderSearchResponse } from '../models/OrderSearchResponse';
import { ReturnOrderSearchRequest } from '../models/ReturnOrderSearchRequest';
import { ReturnOrderSearchResponse } from '../models/ReturnOrderSearchResponse';
import { OrderPackagesResponse } from '../models/OrderPackagesResponse';
import { ManagePackageRequest } from '../models/ManagePackageRequest';
import { ManagePackageResponse } from '../models/ManagePackageResponse';
import { PrintOrderPackageStatus } from '../models/PrintOrderPackageStatus';
import { StatusResponse } from '../models/StatusResponse';
import { DetailProjection } from '../models/DetailProjection';
import { ConsignmentTrackingStatus } from '../models/ConsignmentTrackingStatus';
import { ItemConditionSuccessResponse } from '../models/ItemConditionSuccessResponse';

export const postPurchaseApi = {
  getOrderByOrderId(orderId: string, options?: CustomRequestOptions): Promise<IOrderConfirmationResponse> {
    if (!orderId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getOrderDataReqObj, ...options };
    req.url += `/${orderId}`;
    return handleRequest(req);
  },

  fetchAllOrders(
    { offset, limit, ...rest }: AllOrderRequest,
    options?: CustomRequestOptions,
  ): Promise<OrderSearchResponse | Forbidden> {
    const req = { ...getSearchOrderReqObj, ...options };
    if (!offset.toString() || !limit) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    req.body = JSON.parse(
      JSON.stringify({
        offset,
        limit,
        ...rest,
      }),
    );

    return handleRequest(req);
  },

  getOrderHistory(
    { offset, limit, ...rest }: OrderHistoryRequest,
    options?: CustomRequestOptions,
  ): Promise<OrderHistoryResponse | Forbidden> {
    const req = { ...getOrderHistoryReqObj, ...options };
    if (!offset.toString() || !limit) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    req.params = {
      offset,
      limit,
      projection: 'HISTORY',
      ...rest,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },

  fetchReturnOrders(
    { offset, limit, ...rest }: ReturnOrderSearchRequest,
    options?: CustomRequestOptions,
  ): Promise<ReturnOrderSearchResponse | Forbidden> {
    const req = { ...getSearchReturnOrderReqObj, ...options };
    if ((offset && !offset.toString()) || !limit) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    req.body = JSON.parse(
      JSON.stringify({
        offset,
        limit,
        ...rest,
      }),
    );
    req.params = {
      projection: 'LISTING',
    };

    return handleRequest(req);
  },

  getOrderDetailsByOrderId(
    orderId: string,
    projection?: string,
    options?: CustomRequestOptions,
    hideReturns?: boolean,
  ): Promise<OrderDetailResponse | Forbidden> {
    if (!orderId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getOrderDetailsReqObj, ...options };
    req.url += `/${orderId}`;
    req.params = {
      projection,
      viewReturns: !hideReturns && projection === DetailProjection.Detailed,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },

  getOrderCancellationReasons(options?: CustomRequestOptions): Promise<OrderReasonResponse | Forbidden> {
    const req = { ...getOrderCancellationReasonsReqObj, ...options };
    return handleRequest(req);
  },

  getOrderReturnReasons(
    options?: CustomRequestOptions,
    params?: OrderReturnReasonParams,
  ): Promise<OrderReasonResponse | Forbidden> {
    const req = { ...getReturnReasonsReqObj, ...options };
    const paramsString = req.params;
    const searchParams = new URLSearchParams(paramsString);
    if (params && Object.keys(params).length > 0) {
      searchParams.append('reasonId', params?.reasonId ?? '');
      searchParams?.append('projection', params?.projection ?? '');
      if (params?.query) {
        searchParams.delete('query');
        searchParams.append('query', params?.query ?? '');
      }
      if (params?.reasonType) {
        searchParams.delete('reasonType');
        searchParams.append('reasonType', params?.reasonType ?? '');
      }
    }

    req.params = searchParams;
    return handleRequest(req);
  },

  getProductCondtions(options?: CustomRequestOptions): Promise<ItemConditionSuccessResponse | Forbidden> {
    const req = { ...getProductConditionsReqObj, ...options };
    return handleRequest(req);
  },

  cancelOrderByOrderId(
    payload: CancelOrderRequest,
    options?: CustomRequestOptions,
  ): Promise<CancelOrderResponse | Forbidden> {
    const req = { ...orderCancellationReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  createReturnDraftOrder(
    payload: ReturnOrderRequest,
    options?: CustomRequestOptions,
  ): Promise<CreateReturnOrderResponse | Forbidden> {
    const req = { ...createReturnDraftReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  createReturnOrder(
    payload: ReturnOrderRequest,
    options?: CustomRequestOptions,
  ): Promise<CreateReturnOrderResponse | Forbidden> {
    const req = { ...createReturnReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  getReturnNumber(options?: CustomRequestOptions): Promise<NextNumbersResponse | Forbidden> {
    const req = { ...getReturnNumberReqObj, ...options };
    return handleRequest(req);
  },

  fetchEligibleReturnProducts(
    orderId: string,
    options?: CustomRequestOptions,
  ): Promise<ReturnEligibleItemsResponse | Forbidden> {
    const req = { ...orderReturnEligibleReqObj, ...options };
    req.url += `/${orderId}`;
    return handleRequest(req);
  },

  getReturnDetails(
    returnId: string,
    options?: CustomRequestOptions,
    state?: string,
  ): Promise<ReturnOrderDetailResponse | Forbidden> {
    const req = { ...orderReturnDetailsReqObj, ...options };
    req.url += `/${returnId}`;
    req.params = {
      projection: 'DETAILED',
      ...(state && {
        state,
      }),
    };
    return handleRequest(req);
  },

  cancelReturnByReturnId(
    payload: CancelReturnRequest,
    options?: CustomRequestOptions,
  ): Promise<CancelReturnResponse | Forbidden> {
    const req = { ...returnCancellationReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  returnOrderImagesUpload(
    payload: FormData,
    options?: CustomRequestOptions,
  ): Promise<UploadReturnImageResponse | Forbidden> {
    const req = { ...returnOrderImagesUploadReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, { skipDefaultContentType: true });
  },
  getReturnOrderImages(
    payload: ReturnImageRequest,
    options?: CustomRequestOptions,
  ): Promise<Array<ReturnImageResponseInner> | Forbidden> {
    const req = { ...returnOrderImagesRetrieveReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },
  deleteReturnOrderImages(
    payload: DeleteReturnImageRequest,
    options?: CustomRequestOptions,
  ): Promise<DeleteReturnImageResponse | Forbidden> {
    const req = { ...returnOrderImagesDeleteReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },
  geReturnHistory(
    { offset, limit, ...rest }: OrderHistoryRequest,
    options?: CustomRequestOptions,
  ): Promise<ReturnOrderHistoryResponse | Forbidden> {
    const req = { ...getReturnHistoryReqObj, ...options };
    if (!offset.toString() || !limit) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    req.params = {
      offset,
      limit,
      projection: 'HISTORY',
      ...rest,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },

  getReturnOrderDataByReturnNumber(
    returnId: string,
    orderId: string,
    options?: CustomRequestOptions,
  ): Promise<ReturnOrderDataResponse | Forbidden> {
    const req = { ...orderReturnDataReqObj, ...options };
    req.url += `/${returnId}`;
    req.params = {
      orderId,
    };
    return handleRequest(req);
  },
  getLatamDeliveryTracking(
    { orderId, consignmentNumber, trackingId }: LatamDeliveryTrackingRequest,
    options?: CustomRequestOptions,
  ): Promise<ConsignmentTrackingStatus> {
    const req = { ...getLatamDeliveryTrackingReqObj, ...options };
    req.url = `${req.url}${orderId}/consignments/${consignmentNumber}/track-delivery`;
    req.params = {
      trackingId,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },
  getDeliveryTracking(
    { offset, limit, isAnonymous, ...rest }: DeliveryTrackingRequest,
    options?: CustomRequestOptions,
  ): Promise<TrackDeliveryResponse | Forbidden> {
    const req = isAnonymous
      ? { ...getAnonymousDeliveryTrackingReqObj, ...options }
      : { ...getDeliveryTrackingReqObj, ...options };
    if (!offset.toString() || !limit) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    req.params = {
      offset,
      limit,
      ...rest,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },
  getPackageDetailsByOrderId(
    orderId: string,
    projection?: string,
    options?: CustomRequestOptions,
  ): Promise<OrderConsignmentResponse | Forbidden> {
    if (!orderId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getPacakgeDetailsReqObj, ...options };
    req.url += `/orders/${orderId}/consignments`;
    req.params = {
      projection,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },
  getInvoiceDocument(
    uid: string,
    hash?: string,
    options?: CustomRequestOptions,
  ): Promise<InvoiceDocumentResponse | Forbidden> {
    if (!uid.toString() || !hash) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getInvoiceDocumentReqObj, ...options };
    req.params = {
      uid,
      hash,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },

  getOrderPackageDetails(
    orderId: string,
    projection?: string,
    options?: CustomRequestOptions,
  ): Promise<OrderPackagesResponse | Forbidden> {
    if (!orderId) {
      return Promise.resolve({
        code: 'UI-001',
        message: 'Please check arguments',
      });
    }
    const req = { ...getPacakgeDetailsReqObj, ...options };
    req.url += `/orders/${orderId}/packages`;
    req.params = {
      projection,
    } as unknown as URLSearchParams;
    return handleRequest(req);
  },

  managePackageAction(
    payload: ManagePackageRequest,
    orderId: string,
    packageId: string,
    options?: CustomRequestOptions,
  ): Promise<ManagePackageResponse | Forbidden> {
    const req = { ...managePackageActionReqObj, ...options };
    req.url += `/orders/${orderId}/packages/${packageId}/manage`;
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  getOrdersPrintStatus(
    payload: string[],
    options?: CustomRequestOptions,
  ): Promise<PrintOrderPackageStatus | Forbidden> {
    const req = { ...getOrdersPrintStatusReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },

  saveOrdersPrintStatus(
    payload: PrintOrderPackageStatus,
    options?: CustomRequestOptions,
  ): Promise<StatusResponse | Forbidden> {
    const req = { ...saveOrdersPrintStatusReqObj, ...options };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req);
  },
};

async function handleRequest<T>(req: CustomRequestOptions, augments?: HttpAugments): Promise<T> {
  const response: T = await request(req, augments);
  return Promise.resolve(response);
}

export default postPurchaseApi;
